import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {lastValueFrom, Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import Swal from 'sweetalert2';
import * as routes from '@app/routes';
import {select, Store} from '@ngrx/store';
import {
  CampaignCopy,
  CampaignDelete,
  CancelCampaignLaunch,
  getCancelCampaignLaunchError,
  getCampaignError,
  getPaginatedCampaigns,
  getCampaignSuccess,
  getInfluencersTypes,
  IAuthenticationState,
  ICampaignState,
  IInfluencersState,
  InfluencersTypeList,
  ResetCampaignState,
  CampaignGetAllPaginate,
  getCancelCampaignLaunch,
  ProjectGet,
  CampaignLaunch, getCampaignLaunch, CampaignLaunchCode, getCampaignLaunchCode, getLoggedInUser, getProject, IProjectState, CampaignUpdate
} from '@app/stores';
import {takeUntil} from 'rxjs/operators';
import {InfluencerType} from '@app/modules/shared/models';
import {CampaignCopyComponent} from '@app/modules/campaign/campaign-copy/campaign-copy.component';
import { RenameTitleModalComponent } from '@app/modules/rename-title-modal/rename-title-modal.component';
import { CampaignLaunchHistoryComponent } from '@app/_components/campaign-launch-history/campaign-launch-history.component';
import { CampaignService } from '@app/services/campaign.service';
import {SocketService} from '@app/services/socket.service';
import {TemplateGeneratorService} from '@app/services/template-generator.service';
import {Constants, smartDateFormat} from '@app/consts';

import {ListUrlStateService} from '@app/services/list-url-state.service';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { CreatableSelectComponent } from '../../../_components/creatable-select/creatable-select.component';
import { TitleCasePipe } from '@angular/common';
import { InfoPopoverComponent } from '../../../_components/info-popover/info-popover.component';
import { SbImagePreviewComponent } from '../../../_components/unified/sb-image-preview/sb-image-preview.component';
import { ReLaunchedPopoverComponent } from '../../../_components/re-launched-popover/re-launched-popover.component';
import { CampaignStatusComponent } from '../../../_components/campaign-status/campaign-status.component';
import { CampaignApiSyncComponent } from './campaign-api-sync/campaign-api-sync.component';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { TzDatePipe } from '../../shared/pipes/tz-date.pipe';
import { SbTableComponent } from '@app/_components/unified/sb-table/sb-table.component';
import { SbTableActionsDirective, SbTableCardDirective, SbTableCellDirective, SbTableToolbarDirective } from '@app/_components/unified/sb-table/sb-table.directives';
import { SbTableColumn, SbTableOptions, SbTableServerState } from '@app/_components/unified/sb-table/sb-table.types';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';
@Component({
  selector: 'app-campaigns',
  templateUrl: './campaigns.component.html',
  styleUrls: ['./campaigns.component.scss'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, RouterLink, CreatableSelectComponent, SbTableComponent, SbTableToolbarDirective, SbTableCellDirective, SbTableActionsDirective, SbTableCardDirective, InfoPopoverComponent, SbImagePreviewComponent, ReLaunchedPopoverComponent, CampaignStatusComponent, CampaignApiSyncComponent, MatMenuTrigger, MatMenu, TitleCasePipe, TzDatePipe]
})
export class CampaignsComponent implements OnInit, OnDestroy {
  private listUrlState = inject(ListUrlStateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private templateGeneratorService = inject(TemplateGeneratorService);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private campaignStore = inject<Store<ICampaignState>>(Store);
  private projectStore = inject<Store<IProjectState>>(Store);
  private influencersStore = inject<Store<IInfluencersState>>(Store);
  private readonly sbModal = inject(SbModalService);
  constants = inject(Constants);
  private readonly campaignService = inject(CampaignService);
  private readonly socketService = inject(SocketService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly table = viewChild(SbTableComponent);
  // Ids = the columns the list sorts by (orderBy.column) and the address names (sort=).
  readonly tableColumns: SbTableColumn[] = [
    {id: 'name', header: 'CAMPAIGN NAME', width: '350px', className: 'fw-bold campaign-name'},
    {id: 'designs', header: 'DESIGNS', sortable: false, width: '490px', className: 'campaign-designs'},
    {id: 'influencers_count', header: 'INFLUENCERS', sortable: false, width: '130px', className: 'text-start'},
    {id: 'referrals', header: 'CONVERSIONS', sortable: false, width: '130px', className: 'text-start'},
    {id: 'social_shares', header: 'Social Shares', sortable: false, width: '130px', className: 'text-start'},
    {id: 'open_rate', header: 'OPEN RATE', sortable: false, width: '130px', className: 'text-start'},
    {id: 'status', header: 'STATUS', width: '130px'},
    {id: 'api_sync', header: 'API SYNC', sortable: false, width: '220px', className: 'text-start campaign-api-sync'},
    {id: 'email_subject', header: 'EMAIL SUBJECT', sortable: false, className: 'text-start email-subject'},
  ];
  readonly tableOptions: SbTableOptions = {
    serverSide: true,
    urlState: true,
    urlFilters: () => ({status: this.selectedStatus && this.selectedStatus !== 'all' ? this.selectedStatus : null}),
    pageLength: 100,
    lengthMenu: [10, 25, 50, 100],
    defaultSort: {columnId: 'name', direction: 'asc'},
    selectable: false,
    searching: true,
    tableId: 'campaigns-table',
  };
  readonly totalRows = signal(0);
  routes = routes;
  projectId: number;
  readonly project = signal<any>({});
  selectedTz: any = null;
  campaignCount: boolean;
  /** The page of campaigns the table shows. */
  readonly campaigns = signal<any[]>([]);
  deleteAction = false;
  socketSubscription: any;
  influencerType: any;
  unsubscriber = new Subject();
  readonly influencerTypes = signal<InfluencerType[]>([]);
  copyAction = false;
  viewType = 'list';
  selectedInfluencerType: number;
  selectedStatus: string;
  firstTime = false;
  readonly currentUser = this.authenticationStore.selectSignal(getLoggedInUser);
  isReLaunch = false;
  reLaunchCampaignId: any = null;
  readonly designTypes = [
    {key: 'social_media_post', label: 'Social image'},
    {key: 'email', label: 'Email'},
    {key: 'landing_page', label: 'Landing page'},
  ];
  brokenDesigns = new Set<string>();

  constructor() {
    this.campaignStore.dispatch(ResetCampaignState({params: {error: '', paginatedCampaigns: null}}));
    this.subscribeStore();
    this.subscribeToSocket();
    this.influencerType = 'all';
  }

  subscribeStore() {
    this.projectStore.pipe(select(getProject))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(project => {
        if (project) {
          this.project.set(project);
          this.selectedTz = this.constants.timezone.find(x => x.timeZoneId.toString() === project.timezone?.toString());
        }
      });
    this.campaignStore.pipe(select(getPaginatedCampaigns))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(paginatedCampaigns => {
        if (paginatedCampaigns) {
          if (paginatedCampaigns.list.length > 0 || this.campaignCount) {
            this.campaignCount = true;
            // Copies of the store's rows: socket progress patches them in place (changeCampaignStatus).
            this.campaigns.set(paginatedCampaigns.list.map(campaign => ({...campaign})));
            this.totalRows.set(paginatedCampaigns.paging.total);
          }  else {
            this.campaigns.set([]);
            this.router.navigate([`/projects/${this.projectId}/campaigns/create`]);
          }
        }
      }
      );

    this.campaignStore.pipe(select(getCampaignError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    this.campaignStore.pipe(select(getCampaignSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          if (this.deleteAction) {
            this.deleteAction = false;
            this.reloadCampaigns();
          }
          if (this.copyAction) {
            this.copyAction = false;
            this.reloadCampaigns();
          }
          if (this.isReLaunch) {
            this.isReLaunch = false;
            this.launchCampaign(this.reLaunchCampaignId, {type: 'relaunch'});
          }
        }
      });

    this.campaignStore.pipe(select(getCancelCampaignLaunch))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(data => {
        if (data) {
          this.changeCampaignStatus(data.id, null, data.status);
        }
      });

    this.campaignStore.pipe(select(getCampaignLaunch))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(data => {
        if (data) {
          this.changeCampaignStatus(data.id, 0, 'launching');
        }
      });

    this.campaignStore.pipe(select(getCampaignLaunchCode))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(data => {
        if (data) {
          this.campaignStore.dispatch(CampaignLaunch({campaignId: data.id, code: data.launch_code, reLaunch: true}));
        }
      });


    this.influencersStore.pipe(select(getInfluencersTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(influencerTypes => {
        if (influencerTypes && influencerTypes.length > 0) {
          this.influencerTypes.set(influencerTypes);
        }
      });
  }

  ngOnInit() {
    // Filters from a bookmarked or shared address; the table writes them back after each draw.
    this.selectedStatus = this.listUrlState.read(['status']).filters.status || this.selectedStatus;
    // The table asks for the first page itself (onTableState).
    this.firstTime = true;
    this.route.params.subscribe(params => {
      this.projectId = params.id;
      if (this.projectId) {
        this.projectStore.dispatch(ProjectGet({projectId: this.projectId}));
      }
      this.campaignCount = false;
      if (!this.firstTime) {
        this.reloadCampaigns();
      }
      this.firstTime = false;
      this.getInfluencerTypes();
      // Another project's campaigns, in the same view.
      this.cdr.markForCheck();
    });
  }

  /** The table asks for a page: page, page length, search and sort, sent as the old ajax sent them. */
  onTableState(state: SbTableServerState) {
    const orderBy = state.sort
      ? {column: state.sort.columnId, dir: state.sort.direction}
      : {column: 'name', dir: 'asc'};
    this.getCampaigns(state.page - 1, state.pageLength, state.search, orderBy);
  }

  /** The first page again, through the table, so its pager and the address follow. */
  private reloadCampaigns() {
    this.table()?.reload(true);
  }

  filterCampaignsByInfluencerType(influencerTypeId: any) {
    this.selectedInfluencerType = null;
    if (influencerTypeId) {
      this.selectedInfluencerType = Number(influencerTypeId);
    }
    this.reloadCampaigns();
  }

  filterCampaignsByStatus(status: any = 'all') {
    this.selectedStatus = status;
    this.reloadCampaigns();
  }

  getInfluencerTypes() {
    this.influencersStore.dispatch(InfluencersTypeList());
  }

  getCampaigns(page = 0, perPage = 100, search = '', orderBy = {column: 'name', dir: 'asc'}) {
    this.campaignStore.dispatch(CampaignGetAllPaginate({
      params: {
        project_id: this.projectId,
        ...(this.selectedInfluencerType ? {influencer_type_id: this.selectedInfluencerType} : {}),
        ...((this.selectedStatus && this.selectedStatus !== 'all') ? {status: this.selectedStatus !== 'draft' ? 'not:draft' : this.selectedStatus} : {}),
        options: JSON.stringify({include_project: true, include_segments: true, include_template: true, includePagination: true,
          include_email_stats: true, include_influencer_stats: true, include_sync_influencers_details: true, include_designs: true,
          orderBy}),
        page: page + 1,
        perPage,
        search
      }
    }));
  }

  getInfluencerTypeName(influencerType: number) {
    const type = this.influencerTypes().find(item => Number(item.id) === Number(influencerType));
    return type ? type.name : '';
  }

  deleteCampaign(campaignId: number) {
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.value) {
        this.deleteAction = true;
        this.campaignStore.dispatch(CampaignDelete({campaignId}));
      }
    });
  }

  cancelLaunch(campaignId: number) {
    Swal.fire({
      title: 'Are you sure Want to cancel launch?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.value) {
        this.campaignStore.dispatch(CancelCampaignLaunch({campaignId}));
      }
    });
  }

  launchCampaign(campaignId, params = null) {
    this.router.navigateByUrl(
      routes.launchCampaign({
        projectId: this.projectId,
        campaignId
      }, params)
    );
  }

  isReLaunchable(status: string, launch_code: string): boolean {
    return (['draft', 'launch_failed', 'launched'].includes(status) && !!launch_code);
  }

  isInfoOnly(status: string, launch_code: string): boolean {
    return !this.isReLaunchable(status, launch_code);
  }

  reLaunch(campaignId: number) {
    Swal.fire({
      title: 'Are you sure Want to Re-launch?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.value) {
        this.isReLaunch = true;
        this.reLaunchCampaignId = campaignId;
        const campaign = {
          status: 'draft'
        };
        // this.campaignStore.dispatch(CampaignLaunchCode({campaignId}));
        this.campaignStore.dispatch(CampaignUpdate({campaign, campaignId}));
      }
    });
  }

  getEmailSendProgressInfo(campaign) {
    const failed = campaign.emailsProgress?.failed || 0;
    const sent = campaign.emailsProgress?.sent || 0;
    const total = campaign.emailsProgress?.total || 0;
    const opened = campaign.emailsProgress?.opened || 0;
    const clicked = campaign.emailsProgress?.clicked || 0;
    if (total <= 0) {
      return 'No emails sent';
    }
    if (sent <= 0) {
      return `Delivered: 0%\nSent: 0\nFailed: ${failed}`;
    }
    return `Delivered ${Math.floor(sent * 100 / total)}%\nFailed:${failed}\nSent: ${sent}\nOpened: ${opened}\nClicked: ${clicked}`;
  }

  getEmailSendProgress(campaign) {
    const sent = campaign.emailsProgress?.sent || 0;
    const total = campaign.emailsProgress?.total || 0;
    const opened = campaign.emailsProgress?.opened || 0;
    if (sent <= 0) {
      return `-`;
    }
    return `${Math.floor(opened * 100 / total)}%`;
  }

  getCampaignUpdateInfo(campaign) {
    if (campaign.created_by && !campaign.updated_by) {
      return `Created by ${campaign.created_by?.first_name} ${campaign.created_by?.last_name} - ${smartDateFormat(campaign?.created_at)}`;
    } else if (campaign.updated_by) {
      return `Edited by ${campaign.updated_by?.first_name} ${campaign.updated_by?.last_name} - ${smartDateFormat(campaign?.updated_at)}`;
    }
  }

  getDateTimeBasedOnCampaign(detail, project) {
    return smartDateFormat(detail.launched_at, detail.timezone, null, 'fullDateTimeWithTimeZone', detail.timezone ? detail.timezone : project.timezone);
  }

  copyCampaign(campaignId: number) {
    this.copyAction = true;
    const modelRef = this.sbModal.openComponent(CampaignCopyComponent, {centered: true, size: 'lg', windowClass: 'sb-entity-form-modal'}, {
      projectId: this.projectId,
    });
    modelRef.result.then((result) => {
      const body = {
        name: result.name,
        project_id: Number(result.project_id),
        options: {
          with_influencers: result.with_influencers
        }
      };
      this.campaignStore.dispatch(CampaignCopy({campaignId, body}));
    }, (reason) => {
    });
  }

  smartDateFormat(datetime, inputTimeZone = null, inputFormat = null, outPutFormatName = null, outPutTimeZone = null, returnObject = false) {
    return smartDateFormat(datetime, inputTimeZone, inputFormat, outPutFormatName, outPutTimeZone, returnObject);
  }

  // The table re-creates itself for the other view and asks for its page again.
  changeViewType(type) {
    this.viewType = type;
  }

  hasDesigns(campaign: any): boolean {
    return this.designTypes.some((design) => {
      const url = campaign.designs?.[design.key];
      return !!url && !this.brokenDesigns.has(url);
    });
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
    this.socketSubscription.unsubscribe();
  }

  openRenameModal(entry: any) {
    //
    // Validate entry
    if (!entry?.id) {
      return;
    }

    //
    // Open modal
    const modelRef = this.sbModal.openComponent(RenameTitleModalComponent, {
      centered: true,
      size: 'sm',
      keyboard: true,
      windowClass: 'sb-entity-form-modal',
    }, {
      title: 'Campaign',
      value: entry.name,
    });

    //
    // On close modal event
    // should validate and update the entry
    modelRef.result.then(async (result: any = {}) => {
      //
      // Validate result
      if (!result?.value) {
        return;
      }
      // update database and reload
      await lastValueFrom(this.campaignService.updateCampaign({ name: result.value }, entry.id));
      this.reloadCampaigns();
    });
  }

  onEditCampaignShare(campaign) {
    this.templateGeneratorService.setCampaignValue(campaign);
    this.router.navigateByUrl(
      routes.campaignShare({
        projectId: this.projectId,
        campaignId: campaign.id
      })
    );
  }

  onEditCampaignPreview(campaign) {
    this.templateGeneratorService.setCampaignValue(campaign);
    this.router.navigateByUrl(
      routes.campaignPreview({
        projectId: this.projectId,
        campaignId: campaign.id
      })
    );
  }

  onEditCampaignTemplate(campaign) {
    this.templateGeneratorService.setCampaignValue(campaign);
    if (campaign.template) {
      this.templateGeneratorService.influencerTypeId();
      const params: any = {sourceTemplateId: campaign.template.id};
      this.router.navigateByUrl(
        routes.campaignTemplates({
          projectId: this.projectId,
          campaignId: campaign.id
        }, params)
      );
    } else  {
      this.router.navigateByUrl(
        routes.campaignSelectTemplate({
          projectId: this.projectId,
          campaignId: campaign.id
        })
      );
    }
  }

  subscribeToSocket() {
    this.socketSubscription = this.socketService.sourceData.subscribe((message: any) => {
      if (message?.data?.requesterType === 'campaign') {
        this.changeCampaignStatus(message.data.requesterId, message.data.progress, message.data.status, message.data.result);
      }
    },
    (err) => console.error(err),
    () => console.warn('Completed!')
    );
  }

  // The row is patched in place: the table keeps its rows (and the card view its cards) as they
  // are on each socket tick, so a click on a row (an open menu) is not lost mid-launch. A new
  // array would be a new page to the table, so the view is marked for check instead.
  changeCampaignStatus(campaignId, progress, status, result = null) {
    this.cdr.markForCheck();
    this.campaigns().forEach((item) => {
      if (item.id === campaignId) {
        const mapObj = item;
        mapObj.progress = progress;
        mapObj.status = status;

        if (progress >= 100) {
          delete mapObj.progress;
          delete mapObj.status_message;
          mapObj.status = ['launching', 'draft'].includes(mapObj.status) ? mapObj.status : 'launched';
        }
        if (result) {
          if (result.campaignStatus === 'launch_failed') {
            delete mapObj.progress;
            mapObj.status = result.campaignStatus;
            mapObj.status_message = result.statusMessage;
          }
          if (result.hasOwnProperty('attemptsMade')) {
            mapObj.attemptsMade = result.attemptsMade;
          }
          if (result.campaignStatus === 'launched' && result.redirect) {
            if (result.launched_at) {
              mapObj.launched_at = result.launched_at;
            }
            // if (result.redirect) {
            //   window.open(result.redirect, '_blank');
            // }
          }
        }
      }
    });
  }

  openCampaignLaunchHistory(campaign: any, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.sbModal.openComponent(CampaignLaunchHistoryComponent, {
      centered: false,
      size: 'lg',
      keyboard: true,
    }, {
      campaignId: campaign.id,
      campaignName: campaign.name,
    });
  }
}
